import React, { useState, useMemo } from 'react';
import { useOwnedState } from '../hooks/useOwnedState';
import { analyzeDeal } from '../utils/calculations/dealAnalysis';
import { REGIONAL_MULTIPLIERS } from '../utils/constants/realEstateConstants';
import { formatMoney, formatPercent, signClass } from '../hooks/useResultsAnalysis';

const STORAGE_KEY = 'ultronic_property_comparison';

const COMPARISON_ASSUMPTIONS = {
  downPayment: '20',
  interestRate: '7',
  loanTerm: '30',
  holdingPeriod: '6',
  closingCosts: '3',
  sellingCosts: '6',
  annualAppreciation: '3',
  monthlyExpenses: '300',
};

const STRATEGIES = {
  'Fix & Flip': { key: 'flip', label: 'Fix & Flip' },
  LTR: { key: 'ltr', label: 'Long-term Rental' },
  STR: { key: 'str', label: 'Short-term Rental' },
};

const SAMPLE_PROPERTIES = [
  { id: 1, name: 'Sample: Oak St', purchasePrice: 300000, renovationCost: 47000, expectedSellingPrice: 425000, expectedMonthlyRent: 3400, location: 'NJ', strategy: 'LTR', isActive: true, sample: true },
  { id: 2, name: 'Sample: Mill Rd', purchasePrice: 260000, renovationCost: 60000, expectedSellingPrice: 400000, expectedMonthlyRent: 2900, location: 'NJ', strategy: 'Fix & Flip', isActive: true, sample: true },
];

const BLANK_PROPERTY = { name: '', purchasePrice: '', renovationCost: '', expectedSellingPrice: '', expectedMonthlyRent: '', location: 'NJ', strategy: 'LTR' };

const restoreProperties = (saved) => (Array.isArray(saved) ? saved : SAMPLE_PROPERTIES);

// Runs a property through the same engine as the individual analysis and
// reads the chosen strategy from the same-five-years comparison, so a flip
// and a rental are ranked on the same footing.
function analyzeProperty(property) {
  const analysis = analyzeDeal({ ...COMPARISON_ASSUMPTIONS, ...property });
  const { key } = STRATEGIES[property.strategy] || STRATEGIES.LTR;
  const horizon = analysis.sameHorizon[key];
  const monthlyCashFlow = { flip: null, ltr: analysis.annualCashFlow / 12, str: analysis.annualStrCashFlow / 12 }[key];
  return {
    ...property,
    yearlyReturn: horizon.annualReturn,
    gain: horizon.gain,
    startingCash: analysis.sameHorizon.startingCash,
    monthlyCashFlow,
    years: analysis.sameHorizon.years,
    reinvestmentRate: analysis.sameHorizon.reinvestmentRate,
  };
}

const PropertyComparison = () => {
  const [properties, setProperties] = useOwnedState(STORAGE_KEY, restoreProperties);
  const [newProperty, setNewProperty] = useState(BLANK_PROPERTY);
  const [formError, setFormError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);


  const results = useMemo(() => properties.filter((p) => p.isActive).map(analyzeProperty), [properties]);
  const top = results.length > 1 ? results.reduce((best, current) => (current.yearlyReturn > best.yearlyReturn ? current : best)) : null;
  const hasSamples = properties.some((p) => p.sample);
  const setField = (field) => (e) => setNewProperty({ ...newProperty, [field]: e.target.value });

  const addProperty = () => {
    const flip = (STRATEGIES[newProperty.strategy] || STRATEGIES.LTR).key === 'flip';
    const missing = !newProperty.name ? 'Give the property a name.'
      : !(parseFloat(newProperty.purchasePrice) > 0) ? 'Enter a purchase price.'
        : flip && !(parseFloat(newProperty.expectedSellingPrice) > 0) ? 'A flip needs a value after repairs to be compared.'
          : !flip && !(parseFloat(newProperty.expectedMonthlyRent) > 0) ? 'A rental needs a monthly rent to be compared.'
            : '';
    setFormError(missing);
    if (missing) return;
    setProperties([...properties, {
      id: Date.now(),
      ...newProperty,
      purchasePrice: parseFloat(newProperty.purchasePrice),
      renovationCost: parseFloat(newProperty.renovationCost) || 0,
      expectedSellingPrice: parseFloat(newProperty.expectedSellingPrice) || 0,
      expectedMonthlyRent: parseFloat(newProperty.expectedMonthlyRent) || 0,
      isActive: true,
    }]);
    setNewProperty(BLANK_PROPERTY);
    setShowAddForm(false);
  };

  const toggleProperty = (id) => setProperties(properties.map((p) => (p.id === id ? { ...p, isActive: !p.isActive } : p)));
  const removeProperty = (id) => setProperties(properties.filter((p) => p.id !== id));

  const field = (id, label, props) => (
    <div>
      <label htmlFor={`compare-${id}`} className="block text-sm font-medium text-txt-secondary mb-1">{label}</label>
      <input id={`compare-${id}`} className="terminal-input w-full" value={newProperty[id]} onChange={setField(id)} {...props} />
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-txt-primary mb-2">Property Comparison</h1>
        <p className="text-txt-secondary">Put several properties side by side on the same assumptions.</p>
      </div>

      <div className="bg-surface-primary rounded shadow-glow-green-sm p-4 sm:p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Your Properties</h2>
          <button onClick={() => setShowAddForm(!showAddForm)} className="glow-btn glow-btn-green px-4 py-2 rounded transition-colors">
            {showAddForm ? 'Cancel' : 'Add Property'}
          </button>
        </div>

        {hasSamples && (
          <div className="flex flex-wrap items-center gap-3 border border-terminal-amber/40 rounded px-3 py-2 mb-3">
            <span className="text-terminal-amber font-mono text-xs">The properties marked "Sample" are examples.</span>
            <button onClick={() => setProperties(properties.filter((p) => !p.sample))} className="text-terminal-cyan font-mono text-xs underline min-h-[44px] sm:min-h-0">
              Remove the samples
            </button>
          </div>
        )}

        {showAddForm && (
          <div className="border-t border-surface-border pt-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {field('name', 'Property Name', { type: 'text', placeholder: 'e.g., Main St Duplex' })}
              {field('purchasePrice', 'Purchase Price', { type: 'number', inputMode: 'decimal', placeholder: '300000' })}
              {field('renovationCost', 'Renovation Cost', { type: 'number', inputMode: 'decimal', placeholder: '47000' })}
              {field('expectedSellingPrice', 'Value After Repairs', { type: 'number', inputMode: 'decimal', placeholder: '425000' })}
              {field('expectedMonthlyRent', 'Monthly Rent', { type: 'number', inputMode: 'decimal', placeholder: '3400' })}
              <div>
                <label htmlFor="compare-location" className="block text-sm font-medium text-txt-secondary mb-1">State</label>
                <select id="compare-location" value={newProperty.location} onChange={setField('location')} className="terminal-input w-full">
                  {Object.keys(REGIONAL_MULTIPLIERS).sort().map((state) => <option key={state} value={state}>{state}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="compare-strategy" className="block text-sm font-medium text-txt-secondary mb-1">Strategy</label>
                <select id="compare-strategy" value={newProperty.strategy} onChange={setField('strategy')} className="terminal-input w-full">
                  {Object.entries(STRATEGIES).map(([value, { label }]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </div>
            </div>
            {formError && <p className="text-terminal-amber text-sm font-mono mb-3" role="alert">{formError}</p>}
            <button onClick={addProperty} className="glow-btn glow-btn-green px-6 py-2 rounded transition-colors">Add Property</button>
          </div>
        )}

        <div className="space-y-3 mt-4">
          {properties.length === 0 && <p className="text-sm text-txt-secondary">No properties yet. Add one to start comparing.</p>}
          {properties.map((property) => (
            <div key={property.id} className={`flex items-center justify-between p-3 border rounded ${property.isActive ? 'border-terminal-green bg-terminal-dark-green/20' : 'border-surface-border bg-surface-elevated'}`}>
              <label className="flex items-center gap-3 min-h-[44px] flex-1 cursor-pointer">
                <input type="checkbox" checked={property.isActive} onChange={() => toggleProperty(property.id)} className="h-5 w-5 text-terminal-cyan" />
                <span>
                  <span className="block font-medium text-txt-primary">{property.name}</span>
                  <span className="block text-sm text-txt-secondary">
                    {formatMoney(property.purchasePrice)} / {(STRATEGIES[property.strategy] || STRATEGIES.LTR).label} / {property.location}
                  </span>
                </span>
              </label>
              <button onClick={() => removeProperty(property.id)} aria-label={`Remove ${property.name}`} className="text-terminal-red hover:text-terminal-red-dim text-sm px-3 min-h-[44px]">
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>

      {top && (
        <div className="bg-surface-elevated border border-terminal-dark-green rounded shadow-glow-green-sm p-4 sm:p-6 mb-6">
          <h2 className="text-xl font-semibold text-txt-primary mb-2">Highest Projected Return</h2>
          <p className="text-txt-primary">
            <span className="font-bold text-terminal-green">{top.name}</span> grows its starting cash fastest over {top.years} years
            on these assumptions: <span className={signClass(top.yearlyReturn)}>{formatPercent(top.yearlyReturn)}% a year</span>,
            a gain of {formatMoney(top.gain)} on {formatMoney(top.startingCash)}.
          </p>
          <p className="text-sm text-txt-secondary mt-2">A model estimate, not investment advice.</p>
        </div>
      )}

      {results.length > 0 && (
        <div className="bg-surface-primary rounded shadow-glow-green-sm p-4 sm:p-6">
          <h3 className="text-lg font-semibold mb-4">Side by Side</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-surface-border">
              <thead className="bg-surface-elevated">
                <tr>
                  {['Property', 'Starting Cash', 'Return a Year', 'Gain After 5 Years', 'Monthly Cash Flow'].map((heading) => (
                    <th key={heading} scope="col" className="px-4 py-3 text-left text-xs font-medium text-txt-muted uppercase tracking-wider">{heading}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-surface-primary divide-y divide-surface-border">
                {results.map((property) => (
                  <tr key={property.id}>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-txt-primary">{property.name}</div>
                      <div className="text-sm text-txt-muted">{(STRATEGIES[property.strategy] || STRATEGIES.LTR).label}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-txt-primary">{formatMoney(property.startingCash)}</td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${signClass(property.yearlyReturn)}`}>{formatPercent(property.yearlyReturn)}%</td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${signClass(property.gain)}`}>{formatMoney(property.gain)}</td>
                    <td className={`px-4 py-4 whitespace-nowrap text-sm ${property.monthlyCashFlow === null ? 'text-txt-muted' : signClass(property.monthlyCashFlow)}`}>
                      {property.monthlyCashFlow === null ? 'None (sold)' : formatMoney(property.monthlyCashFlow)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-txt-secondary mt-4">
            Every property is measured over the same five years: what its starting cash becomes, with a flip's proceeds and any
            rental cash flow earning {results[0].reinvestmentRate}% a year once they are out of the property. Assumes 20% down, a 7%
            30-year loan, 3% purchase closing costs, 6% selling costs, 3% annual appreciation, a 6-month flip, $300 a month of
            utilities and upkeep while a flip is held, and property tax and insurance estimated at 1.2% and 0.5% of the price.
            Short-term rental revenue uses the average nightly rate for the state. Use Individual Analysis to change any of these.
          </p>
        </div>
      )}
    </div>
  );
};

export default PropertyComparison;
